import type { AsyncSequence, GUID } from "@pdfking/tfunc"
import { Broadcast, FullMethodDispatcher, JSONEncode, ProcessIterable, UUID } from "@pdfking/tfunc"

import type { MessageRouter } from "#public"
import type { ReceiverInput } from "#public"
import type { ReceiverMessage } from "#public"
import type { ReceiverOutput } from "#public"
import type { WorkerReceiverAPI } from "#public"

import { ReceiverAction } from "../types/ReceiverAction"

const StreamToReceiver = <T, R>(
  stream: AsyncSequence<T>,
  { send }: WorkerReceiverAPI<T, R>,
  channel_id: GUID
): Promise<void> =>
  ProcessIterable(
    stream,
    data =>
      send({
        channel_id,
        data,
        enum: ReceiverAction.YIELD,
      }),
    error =>
      send({
        channel_id,
        enum: ReceiverAction.STOP,
        error,
      })
  )

const forward_to = async <T, R>(
  stream: ReceiverInput<T>,
  worker: WorkerReceiverAPI<T, R>,
  channel_id: GUID,
  ...extra: ReceiverMessage<T>[]
) => {
  for (const message of extra) {
    worker.send(message)
  }

  await StreamToReceiver(stream, worker, channel_id)
}

export const WorkerForward = <T, R>(
  worker: WorkerReceiverAPI<T, R>,
  forward: MessageRouter<T, R>
): MessageRouter<T, R> => {
  const channels: Map<GUID, ReturnType<typeof Broadcast<R | T>>> = new Map()

  const get_broadcast = (channel: GUID) => {
    let current = channels.get(channel)
    if (!current) channels.set(channel, (current = Broadcast<R | T>()))
    return current
  }

  worker.on(
    FullMethodDispatcher<[ReceiverMessage<R | T>], void>(
      {
        [ReceiverAction.START]: ({ request_channel, response_channel }) => {
          const stream = get_broadcast(request_channel).subscribe()

          void forward_to(
            forward(stream as ReceiverInput<T>) as ReceiverInput<T>,
            worker,
            response_channel
          )
        },

        [ReceiverAction.STOP]: ({ channel_id, error }) => {
          channels.get(channel_id)!.stop(error)
          channels.delete(channel_id)
        },

        [ReceiverAction.YIELD]: ({ channel_id, data }) => channels.get(channel_id)!.push(data),
      },
      m => console.warn(`WorkerForward: ignored message ${JSONEncode(m)}`)
    )
  )

  return async function* (request: ReceiverInput<T>): ReceiverOutput<R> {
    //  new request is incoming, we forward the request to the worker and return a response.

    const request_channel = UUID()
    const response_channel = UUID()

    const stream = get_broadcast(response_channel).subscribe()

    void forward_to(request, worker, request_channel, {
      enum: ReceiverAction.START,
      request_channel,
      response_channel,
    })

    yield* stream as ReceiverOutput<R>
  }
}
